// Sessão de dispositivo (5.4): guardada em localStorage após a troca do link.
// Este módulo é puro (sem React) para ser usado tanto pela camada de API quanto pelas telas.
import type { DeviceSession } from "@centro-cirurgico/contracts";

export const DEVICE_SESSION_KEY = "cccontrol_device_session";
export const DEVICE_ROOM_KEY = "cccontrol_device_room";
/**
 * Token do link de acesso aberto neste aparelho. Com ele a sessão (que expira em
 * horas) é renovada sozinha, e o dispositivo segue funcionando até o link ser
 * revogado, sem precisar abrir o link de novo.
 */
export const DEVICE_LINK_KEY = "cccontrol_device_link";
const INVALID_REASON_KEY = "cccontrol_device_session_invalid";

/** Evento disparado em `window` sempre que a sessão muda (gravada ou limpa). */
export const DEVICE_SESSION_EVENT = "cccontrol:device-session";

export type SessionInvalidReason = "revoked" | "expired" | "missing" | "cleared";

export interface DeviceSessionEventDetail {
  session: DeviceSession | null;
  reason: SessionInvalidReason | null;
}

function storage(): Storage | null {
  try {
    if (typeof globalThis.localStorage === "undefined") return null;
    return globalThis.localStorage;
  } catch {
    // Safari em modo privado / storage bloqueado.
    return null;
  }
}

function emit(detail: DeviceSessionEventDetail) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  if (typeof CustomEvent !== "function") return;
  window.dispatchEvent(new CustomEvent<DeviceSessionEventDetail>(DEVICE_SESSION_EVENT, { detail }));
}

function isSession(value: unknown): value is DeviceSession {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.token === "string" &&
    typeof candidate.expiresAt === "string" &&
    (candidate.mode === "reception" || candidate.mode === "room") &&
    typeof candidate.hospitalId === "string"
  );
}

function isExpired(session: DeviceSession, now = Date.now()): boolean {
  const expiresAt = Date.parse(session.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= now;
}

/** Lê a sessão válida; sessões expiradas são removidas e retornam `null`. */
export function readDeviceSession(): DeviceSession | null {
  const store = storage();
  if (!store) return null;
  const raw = store.getItem(DEVICE_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSession(parsed)) {
      store.removeItem(DEVICE_SESSION_KEY);
      return null;
    }
    if (isExpired(parsed)) {
      clearDeviceSession("expired");
      return null;
    }
    return parsed;
  } catch {
    store.removeItem(DEVICE_SESSION_KEY);
    return null;
  }
}

export function storeDeviceSession(session: DeviceSession): void {
  const store = storage();
  if (store) {
    store.setItem(DEVICE_SESSION_KEY, JSON.stringify(session));
    store.removeItem(INVALID_REASON_KEY);
  }
  emit({ session, reason: null });
}

export function clearDeviceSession(reason: SessionInvalidReason = "cleared"): void {
  const store = storage();
  if (store) {
    store.removeItem(DEVICE_SESSION_KEY);
    store.removeItem(DEVICE_ROOM_KEY);
    if (reason === "revoked" || reason === "expired") store.setItem(INVALID_REASON_KEY, reason);
    else store.removeItem(INVALID_REASON_KEY);
  }
  emit({ session: null, reason });
}

/** Motivo da última invalidação (para mostrar "Link revogado ou expirado" após recarregar). */
export function readInvalidReason(): SessionInvalidReason | null {
  const value = storage()?.getItem(INVALID_REASON_KEY);
  return value === "revoked" || value === "expired" ? value : null;
}

export function readStoredLinkToken(): string | null {
  return storage()?.getItem(DEVICE_LINK_KEY) ?? null;
}

export function storeLinkToken(token: string): void {
  storage()?.setItem(DEVICE_LINK_KEY, token);
}

export function clearLinkToken(): void {
  storage()?.removeItem(DEVICE_LINK_KEY);
}

export function readStoredRoomId(): string | null {
  return storage()?.getItem(DEVICE_ROOM_KEY) ?? null;
}

export function storeRoomId(roomId: string): void {
  storage()?.setItem(DEVICE_ROOM_KEY, roomId);
}

export function clearStoredRoomId(): void {
  storage()?.removeItem(DEVICE_ROOM_KEY);
}

/** Assina mudanças de sessão nesta aba e em outras abas (evento `storage`). */
export function subscribeDeviceSession(listener: (detail: DeviceSessionEventDetail) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const onCustom = (event: Event) => {
    listener((event as CustomEvent<DeviceSessionEventDetail>).detail);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== DEVICE_SESSION_KEY) return;
    listener({ session: readDeviceSession(), reason: readInvalidReason() });
  };
  window.addEventListener(DEVICE_SESSION_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(DEVICE_SESSION_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}
